import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';

const LegalShell = ({ title, lastUpdated, children }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-100">
    <header className="border-b border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-zinc-950" />
          </div>
          <span className="font-bold text-lg">Home <span className="text-amber-400">MDB 2A</span></span>
        </Link>
        <Link to="/" className="text-sm text-zinc-400 hover:text-amber-400 inline-flex items-center gap-1.5"><ArrowLeft className="w-3.5 h-3.5" /> Accueil</Link>
      </div>
    </header>
    <div className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold mb-2 tracking-tight">{title}</h1>
      <p className="text-sm text-zinc-500 mb-10">Dernière mise à jour : {lastUpdated}</p>
      <div className="prose prose-invert prose-zinc max-w-none [&_h2]:text-amber-400 [&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:font-bold [&_h2]:text-xl [&_h3]:text-zinc-200 [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-zinc-300 [&_p]:leading-relaxed [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-zinc-300 [&_ul]:mb-4 [&_li]:mb-2 [&_strong]:text-amber-300 [&_a]:text-amber-400 [&_a]:underline">
        {children}
      </div>
      <div className="mt-12 pt-8 border-t border-zinc-900 text-sm text-zinc-500">
        <p>Pour toute question relative à ce document, contacte <a href="mailto:contact@homemdb2a.fr" className="text-amber-400 underline">contact@homemdb2a.fr</a>.</p>
      </div>
    </div>
  </div>
);

export const TermsOfUsePage = () => (
  <LegalShell title="Conditions Générales d'Utilisation" lastUpdated="5 mai 2026">
    <p>Les présentes Conditions Générales d'Utilisation (« <strong>CGU</strong> ») régissent l'utilisation de la plateforme <strong>Home MDB 2A</strong> (ci-après « la Plateforme ») éditée par Axel AGUANNO, marchand de biens, dont les coordonnées figurent sur la page Mentions légales.</p>

    <h2>1. Objet</h2>
    <p>Home MDB 2A est un logiciel SaaS (Software as a Service) destiné aux Marchands de Biens professionnels, leur permettant de gérer la prospection, l'étude de rentabilité, la visite technique, le suivi des travaux et la commercialisation de biens immobiliers.</p>

    <h2>2. Acceptation</h2>
    <p>L'inscription sur la Plateforme entraîne l'acceptation pleine et entière des présentes CGU. L'utilisateur déclare avoir au moins 18 ans et la capacité juridique de contracter.</p>

    <h2>3. Compte utilisateur</h2>
    <p>L'utilisateur est responsable de la confidentialité de ses identifiants. Toute utilisation du compte est réputée effectuée par son titulaire. En cas de soupçon d'usage frauduleux, le Service doit être informé sans délai.</p>

    <h2>4. Disponibilité du service</h2>
    <p>L'éditeur fait ses meilleurs efforts pour assurer une disponibilité continue, sans garantir une disponibilité 100%. Des opérations de maintenance pourront survenir, avec préavis raisonnable lorsque cela est possible.</p>

    <h2>5. Propriété intellectuelle</h2>
    <p>L'ensemble des éléments de la Plateforme (code, design, marque, contenus pédagogiques) est la propriété exclusive de l'éditeur. Toute reproduction non autorisée est interdite.</p>
    <p>Les <strong>données saisies par l'utilisateur</strong> (biens, projets, BPU, factures, photos, etc.) demeurent <strong>la propriété exclusive de l'utilisateur</strong>. Une licence non-exclusive est accordée à l'éditeur uniquement pour les besoins d'exécution du service.</p>

    <h2>6. Comportement de l'utilisateur</h2>
    <p>L'utilisateur s'engage à ne pas utiliser la Plateforme à des fins illégales, ne pas tenter de compromettre sa sécurité, et respecter les droits des tiers (notamment droit à l'image pour les photos uploadées).</p>

    <h2>7. Suspension / résiliation</h2>
    <p>En cas de manquement grave aux CGU, le compte pourra être suspendu sans préavis. L'utilisateur peut résilier son compte à tout moment depuis l'app.</p>

    <h2>8. Limitation de responsabilité</h2>
    <p>Home MDB 2A est un outil d'aide à la décision. Les calculs (rentabilité, marge, fiscalité) sont fournis à titre indicatif. <strong>L'éditeur ne se substitue pas à un expert-comptable, un fiscaliste ou un notaire</strong>. L'utilisateur est seul responsable de ses décisions d'investissement.</p>

    <h2>9. Modification des CGU</h2>
    <p>L'éditeur peut modifier les CGU à tout moment. L'utilisateur sera notifié par email au moins 15 jours avant l'entrée en vigueur des modifications, et invité à les accepter pour continuer à utiliser le service.</p>

    <h2>10. Loi applicable</h2>
    <p>Les présentes CGU sont régies par le droit français. Tout litige relèvera des tribunaux compétents du ressort du siège de l'éditeur.</p>
  </LegalShell>
);

export const TermsOfSalePage = () => (
  <LegalShell title="Conditions Générales de Vente" lastUpdated="5 mai 2026">
    <p>Les présentes Conditions Générales de Vente (« <strong>CGV</strong> ») régissent les abonnements à la Plateforme <strong>Home MDB 2A</strong>.</p>

    <h2>1. Plans et tarifs</h2>
    <ul>
      <li><strong>Solo Mensuel</strong> : 39€ TTC par mois</li>
      <li><strong>Solo Annuel</strong> : 390€ TTC par an (équivalent ≈32,50€/mois, soit 2 mois offerts)</li>
    </ul>
    <p>Les prix sont exprimés en Euros, toutes taxes comprises. La TVA française est appliquée pour les clients résidant en France métropolitaine et DROM-COM.</p>

    <h2>2. Essai gratuit</h2>
    <p>L'inscription donne droit à <strong>14 jours d'essai gratuit</strong> sans carte bancaire requise et sans engagement. À l'issue de l'essai, l'accès aux fonctionnalités est suspendu si aucun abonnement n'est souscrit. Les données sont conservées 30 jours puis supprimées (sauf demande de réactivation).</p>

    <h2>3. Souscription et paiement</h2>
    <p>Le paiement s'effectue par carte bancaire via la solution sécurisée <strong>Stripe</strong> (PCI DSS Level 1). Aucune donnée de carte n'est stockée sur nos serveurs.</p>
    <p>L'abonnement se renouvelle automatiquement à chaque échéance. Une facture est envoyée par email après chaque débit réussi.</p>

    <h2>4. Droit de rétractation</h2>
    <p>Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux abonnements à des services numériques pleinement exécutés avec l'accord exprès du consommateur. Toutefois, l'éditeur applique de manière volontaire une <strong>politique satisfait ou remboursé pendant 14 jours</strong> à compter de la première souscription payante : remboursement intégral sur simple demande.</p>

    <h2>5. Résiliation</h2>
    <p>L'utilisateur peut résilier son abonnement à tout moment depuis le portail Stripe accessible dans Paramètres &gt; Abonnement. La résiliation est effective à la fin de la période en cours déjà payée. Aucun remboursement prorata sauf cas mentionné en Article 4.</p>

    <h2>6. Modification du tarif</h2>
    <p>Les tarifs peuvent évoluer pour les nouveaux abonnés. Les <strong>abonnés existants conservent leur tarif d'origine</strong> tant qu'ils maintiennent leur abonnement actif sans interruption (pricing fondateur garanti à vie).</p>

    <h2>7. Données et portabilité</h2>
    <p>L'utilisateur peut à tout moment exporter ses données depuis l'app (format JSON + PDF). En cas de résiliation, les données sont conservées 30 jours puis supprimées définitivement.</p>

    <h2>8. Litiges</h2>
    <p>En cas de réclamation, contacte <a href="mailto:contact@homemdb2a.fr">contact@homemdb2a.fr</a>. Médiateur de la consommation : voir Mentions Légales.</p>
  </LegalShell>
);

export const PrivacyPolicyPage = () => (
  <LegalShell title="Politique de confidentialité (RGPD)" lastUpdated="5 mai 2026">
    <p>La présente politique décrit la manière dont Home MDB 2A collecte, traite et protège tes données personnelles, conformément au <strong>Règlement Général sur la Protection des Données (RGPD)</strong> et à la <strong>Loi Informatique et Libertés</strong>.</p>

    <h2>1. Responsable du traitement</h2>
    <p>Axel AGUANNO — éditeur de Home MDB 2A. Coordonnées sur la page Mentions légales.</p>

    <h2>2. Données collectées</h2>
    <h3>Lors de l'inscription</h3>
    <ul>
      <li>Nom complet, email, mot de passe (hashé bcrypt, jamais stocké en clair)</li>
      <li>Société (optionnel)</li>
    </ul>
    <h3>Lors de l'utilisation</h3>
    <ul>
      <li>Données métier que tu saisis : biens, projets, études, visites, photos, factures, contacts artisans</li>
      <li>Logs techniques (IP, user agent, horodatage) pour la sécurité et le débogage</li>
    </ul>
    <h3>Lors du paiement</h3>
    <ul>
      <li>Aucune donnée de carte bancaire n'est traitée par nos serveurs. Tout est délégué à <strong>Stripe</strong> (certifié PCI DSS Level 1).</li>
      <li>Nom, adresse de facturation, montant — pour la conformité comptable</li>
    </ul>

    <h2>3. Finalités</h2>
    <ul>
      <li>Fourniture du service d'aide à la gestion MDB</li>
      <li>Facturation et obligations comptables</li>
      <li>Support technique et communication transactionnelle (rappels, factures)</li>
      <li>Amélioration produit (analytics anonymisés)</li>
    </ul>

    <h2>4. Base légale</h2>
    <p>Le traitement repose sur l'<strong>exécution du contrat</strong> (CGU) que tu as accepté à l'inscription, et sur le <strong>respect des obligations légales</strong> (comptabilité, fiscalité).</p>

    <h2>5. Hébergement</h2>
    <p>Toutes les données sont hébergées <strong>en Europe (Union Européenne 🇪🇺)</strong> sur des infrastructures conformes RGPD. Aucun transfert hors UE.</p>

    <h2>6. Durée de conservation</h2>
    <ul>
      <li><strong>Compte actif</strong> : conservation tant que le compte existe</li>
      <li><strong>Après résiliation</strong> : 30 jours de "soft delete" pour réactivation, puis suppression définitive</li>
      <li><strong>Factures</strong> : 10 ans (obligation comptable légale)</li>
      <li><strong>Logs techniques</strong> : 1 an glissant</li>
    </ul>

    <h2>7. Tes droits</h2>
    <p>Conformément au RGPD, tu disposes des droits suivants :</p>
    <ul>
      <li><strong>Droit d'accès</strong> : obtenir une copie de tes données</li>
      <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
      <li><strong>Droit à l'effacement</strong> ("droit à l'oubli") : suppression de ton compte et de tes données</li>
      <li><strong>Droit à la portabilité</strong> : export de tes données dans un format structuré (JSON)</li>
      <li><strong>Droit d'opposition</strong> : t'opposer à un traitement spécifique</li>
      <li><strong>Droit de limitation</strong> : geler temporairement le traitement</li>
    </ul>
    <p>Pour exercer ces droits : <a href="mailto:contact@homemdb2a.fr">contact@homemdb2a.fr</a>. Réponse sous 30 jours.</p>

    <h2>8. Cookies</h2>
    <p>Home MDB 2A utilise uniquement des cookies <strong>techniques nécessaires</strong> au fonctionnement (authentification, préférences). Aucun cookie publicitaire ou de tracking tiers n'est déposé sans ton consentement explicite.</p>

    <h2>9. Sous-traitants</h2>
    <ul>
      <li><strong>Stripe</strong> (paiement) — USA, contrat DPA conforme</li>
      <li><strong>Resend</strong> (envoi email transactionnel) — UE</li>
    </ul>

    <h2>10. Réclamation CNIL</h2>
    <p>Si tu estimes que tes droits ne sont pas respectés, tu peux saisir la <strong>CNIL</strong> : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>.</p>
  </LegalShell>
);

export const LegalNoticePage = () => (
  <LegalShell title="Mentions légales" lastUpdated="5 mai 2026">
    <h2>Éditeur</h2>
    <p>
      <strong>Axel AGUANNO</strong><br />
      Marchand de biens<br />
      Email : <a href="mailto:contact@homemdb2a.fr">contact@homemdb2a.fr</a>
    </p>
    <p className="text-xs text-zinc-500 italic">À compléter avant lancement public : adresse postale, SIRET, numéro de TVA intracommunautaire, numéro carte professionnelle (carte T pour transactions immobilières si applicable).</p>

    <h2>Directeur de la publication</h2>
    <p>Axel AGUANNO</p>

    <h2>Hébergeur</h2>
    <p>L'application est hébergée sur des infrastructures sécurisées en Europe.</p>

    <h2>Médiateur de la consommation</h2>
    <p>Conformément aux dispositions du Code de la consommation concernant le règlement amiable des litiges, le client peut recourir au service de médiation : à compléter.</p>

    <h2>Propriété intellectuelle</h2>
    <p>L'ensemble des contenus du site (textes, graphismes, logos, code) est la propriété exclusive d'Axel AGUANNO, sauf mention contraire. Toute reproduction non autorisée est interdite.</p>
  </LegalShell>
);
